import React, { useState, useMemo } from 'react';
import { Lead } from '../../types/Lead';
import { useScoring } from '../../contexts/ScoringContext';
import LeadCardHeader from './LeadCardHeader';
import LeadCardContent from './LeadCardContent';

interface LeadCardProps {
  lead: Lead;
  isExpanded: boolean;
  onToggle: () => void;
  onLeadUpdate?: (updatedLead: Lead) => void;
  isStarred?: boolean;
  onStarChange?: (isStarred: boolean) => void;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
}

const LeadCard: React.FC<LeadCardProps> = ({
  lead,
  isExpanded,
  onToggle,
  isStarred = false,
  onStarChange,
  onMemberChange
}) => {
  // Add refresh trigger to sync both MemberManager instances
  const [memberRefreshTrigger, setMemberRefreshTrigger] = useState(0);
  const { calculateCustomScore, weights } = useScoring();

  // Calculate the custom score for this lead (memoized for performance)
  const customScore = useMemo(() => {
    try {
      // Safety check for required lead properties
      if (!lead || !lead.processed || !lead.overallScore || !lead.metrics) {
        console.warn('Lead missing required properties:', lead);
        return 0;
      }

      const score = calculateCustomScore(lead);
      // Validate the score to prevent NaN
      if (typeof score !== 'number' || isNaN(score) || !isFinite(score)) {
        console.warn('Invalid custom score calculated for lead:', lead.metadata?.companyName, score);
        return lead.processed?.averageScore || 0;
      }
      return score;
    } catch (error) {
      console.error('Error calculating custom score:', error);
      return lead.processed?.averageScore || 0;
    }
  }, [calculateCustomScore, lead, weights]); // eslint-disable-line react-hooks/exhaustive-deps

  const getScoreBgColor = (score: number): string => {
    if (score >= 7) return 'rgba(34, 197, 94, 0.05)';   // Green for 7-10
    if (score >= 5) return 'rgba(245, 158, 11, 0.05)';  // Amber for 5-6.9
    if (score >= 3) return 'rgba(239, 68, 68, 0.05)';   // Red for 3-4.9
    return 'rgba(107, 114, 128, 0.05)';                 // Gray for 1-2.9
  };

  const getScoreBorderColor = (score: number): string => {
    if (score >= 7) return '#bbf7d0';   // Green for 7-10
    if (score >= 5) return '#fef3c7';   // Amber for 5-6.9
    if (score >= 3) return '#fecaca';   // Red for 3-4.9
    return '#e5e7eb';                   // Gray for 1-2.9
  };

  // Safety check for required lead properties
  if (!lead || !lead.metadata || !lead.processed) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="text-center text-gray-500">
          <p>Invalid lead data</p>
        </div>
      </div>
    );
  }

  // Handler to trigger refresh in both MemberManager instances
  const handleMembersUpdate = () => {
    setMemberRefreshTrigger(prev => prev + 1);

    // Notify parent about member changes if callback is provided
    if (onMemberChange && lead.id) {
      // We need to check if the current user is now a member
      // This will be handled by the MemberManager component
      // For now, we'll trigger a refresh of the parent's member status
      console.log(`🔄 Member update for lead ${lead.id}, notifying parent`);
    }
  };

  return (
    <div style={{
      border: `2px solid ${getScoreBorderColor(customScore)}`,
      borderRadius: '8px',
      marginBottom: '24px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      backgroundColor: getScoreBgColor(customScore),
      transition: 'all 0.3s ease',
      overflow: 'hidden'
    }}>
      <LeadCardHeader
        lead={lead}
        isExpanded={isExpanded}
        onToggle={onToggle}
        onMembersUpdate={handleMembersUpdate}
        memberRefreshTrigger={memberRefreshTrigger}
        isStarred={isStarred}
        onStarChange={onStarChange}
        customScore={customScore}
        onMemberChange={onMemberChange}
      />
      {isExpanded && <LeadCardContent
        lead={lead}
        onMembersUpdate={handleMembersUpdate}
        memberRefreshTrigger={memberRefreshTrigger}
      />}
    </div>
  );
};

export default LeadCard;